import React from "react";

import classnames from "classnames";
import styled from "styled-components";

import questionMarkSvg from "../assets/svg/questionMark.svg";
import clipboardSvg from "../assets/svg/clipboard.svg";
import clockArrowSvg from "../assets/svg/clockArrow.svg";
import dollarSignSvg from "../assets/svg/dollarSignCircled.svg";
import lightbulbSvg from "../assets/svg/lightbulb.svg";
import speechSvg from "../assets/svg/speechBubbles.svg";
import thumbsUpSvg from "../assets/svg/thumbsUp.svg";
import warningSvg from "../assets/svg/warningSign.svg";

import { ReactComponent as CloseIcon } from "../assets/svg/closeIcon.svg";

type Datum = {
  svgSrc: string;
  title: string;
  description: string;
  tip: React.ReactNode;
};

const BUSINESS_DATA: Datum[] = [
  {
    svgSrc: speechSvg,
    title: "Consultation",
    description: "What evidence did you use? Who did you consult with?",
    tip: null,
  },
  {
    svgSrc: questionMarkSvg,
    title: "The problem",
    description: "Why are you doing this?",
    tip: (
      <>
        To support your case you should include key workplace statistics and
        specific information about the health status of your employees to help
        emphasise your point (e.g. sick leave rates, health survey results,
        levels of overweight/obesity). Use statistics from our{" "}
        <a
          className="text-reset text-underline"
          href="https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-business-case-for-workplace-health.pdf"
          download="Business case brochure"
          title="Download your business case brochure"
        >
          Business Case Brochure
        </a>{" "}
        to help make your point.
      </>
    ),
  },
  {
    svgSrc: lightbulbSvg,
    title: "The solution",
    description: "What do you plan to do and achieve?",
    tip: null,
  },
  {
    svgSrc: thumbsUpSvg,
    title: "The benefits",
    description: "What are the gains for business?",
    tip: (
      <>
        Use examples like increased productivity, improved business reputation,
        reduced sick leave and staff turnover.
      </>
    ),
  },
  {
    svgSrc: warningSvg,
    title: "The risks",
    description: "What could go wrong and how will you minimise these risks?",
    tip: null,
  },
  {
    svgSrc: dollarSignSvg,
    title: "The cost",
    description:
      "Include out of pocket expenses as well as in-kind (e.g. hours to coordinate).",
    tip: (
      <>
        Workplace health and wellbeing strategies don’t need to cost a lot.
        There are many free or low cost strategies and these should be included
        in your business case. Unlock our{" "}
        <a
          className="text-reset text-underline"
          href="/health-professionals/workplaces/quick-wins"
          target="_blank"
          rel="noopener noreferrer"
        >
          Quick Wins
        </a>{" "}
        for some inspiration.
      </>
    ),
  },
  {
    svgSrc: clockArrowSvg,
    title: "The timeline",
    description:
      "What are the major milestones and when will they be completed?",
    tip: null,
  },
  {
    svgSrc: clipboardSvg,
    title: "Evaluation",
    description: "How will you measure and report results?",
    tip: null,
  },
];

export const BusinessCircles: React.FC<{}> = () => {
  const [rotateSatellites, setRotateSatellites] = React.useState<number>(0);
  const [showInfo, setShowInfo] = React.useState<boolean>(false);

  const changeButton = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (showInfo) {
      setShowInfo(false);
    }
    const { value } = e.currentTarget;
    setRotateSatellites(parseInt(value, 10));
  };

  const toggleInfo = () => {
    setShowInfo((prev) => !prev);
  };

  return (
    <OuterWrapper>
      <InnerWrapper className="bg-info">
        <h3 className="text-white text-center">
          {BUSINESS_DATA[rotateSatellites].description}
        </h3>
        {BUSINESS_DATA[rotateSatellites].tip && (
          <InfoButton
            onClick={toggleInfo}
            active={showInfo}
            className={classnames("mt-2", {
              "bg-info": showInfo,
              "bg-warning": !showInfo,
            })}
          >
            {showInfo ? (
              <OpenTip>
                <strong className="text-uppercase text-gold">Tip</strong>
                <span className="d-block mb-3">
                  {BUSINESS_DATA[rotateSatellites].tip}
                </span>
                <CloseIcon width={30} height={30} fill="white" />
              </OpenTip>
            ) : (
              "i"
            )}
          </InfoButton>
        )}
      </InnerWrapper>
      {BUSINESS_DATA.map(({ svgSrc, title }, idx) => (
        <SatelliteWrapper
          key={idx}
          rotation={idx}
          rotateSatellites={rotateSatellites}
        >
          <SatelliteContent rotation={idx} rotateSatellites={rotateSatellites}>
            <SatelliteIcon
              value={idx}
              onClick={changeButton}
              active={idx === rotateSatellites}
            >
              <img src={svgSrc} alt={`${title} icon`} />
            </SatelliteIcon>
            <SatelliteTitle active={idx === rotateSatellites}>
              {title}
            </SatelliteTitle>
          </SatelliteContent>
        </SatelliteWrapper>
      ))}
    </OuterWrapper>
  );
};

const OuterWrapper = styled.div`
  position: relative;
  margin: 2rem auto;
  width: 45rem;
  height: 45rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(-90deg);
`;

const InnerWrapper = styled.div`
  position: relative;
  padding: 1rem 2rem;
  width: 50%;
  height: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  border: 1.5rem solid lightgrey;
  border-radius: 50%;
  transform: rotate(90deg);
  &::after {
    content: "";
    position: absolute;
    top: calc(-10rem - 2%);
    bottom: calc(-10rem - 2%);
    left: calc(-10rem - 2%);
    right: calc(-10rem - 2%);
    border: 1px dashed grey;
    border-radius: 50%;
  }
`;

const SatelliteWrapper = styled.div<{
  rotation: number;
  rotateSatellites: number;
}>`
  position: absolute;
  width: 18%;
  height: 18%;
  border-radius: 50%;
  transition: 500ms all cubic-bezier(0.34, 1.25, 0.64, 1);
  transform: ${(props) =>
    `rotate(${
      ((props.rotation - props.rotateSatellites) / 8) * 360
    }deg) translate(20rem)`};
`;

const SatelliteContent = styled.div<{
  rotation: number;
  rotateSatellites: number;
}>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: 300ms all cubic-bezier(0.34, 1.56, 0.64, 1);
  transform: ${(props) =>
    `rotate(${
      (props.rotateSatellites + 1) * 90 -
      ((props.rotation + props.rotateSatellites) / 8) * 360
    }deg)`};
`;

const SatelliteIcon = styled.button<{ active: boolean }>`
  border-radius: 50%;
  width: 60%;
  height: 60%;
  padding: 1rem;
  border: none;
  background-color: ${(p) => (p.active ? p.theme.info : p.theme.gray800)};
  &:focus {
    outline: none;
  }
`;

const SatelliteTitle = styled.strong<{ active: boolean }>`
  color: ${(p) => (p.active ? p.theme.info : p.theme.gray800)};
`;

const InfoButton = styled.button<{ active: boolean }>`
  position: absolute;
  left: ${(props) => (props.active ? "-25%" : "calc(50% - 1rem)")};
  bottom: ${(props) => (props.active ? "-25%" : "2rem")};
  width: ${(props) => (props.active ? "150%" : "2rem")};
  height: ${(props) => (props.active ? "150%" : "2rem")};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  font-weight: 500;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  z-index: 1;
  overflow: hidden;
  transition: 400ms all ease;
  &:focus {
    outline: ${(props) => (props.active ? "none" : "auto")};
  }
`;

const OpenTip = styled.small`
  padding: 1rem 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
